// Third party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Form, FormikProps, withFormik } from 'formik';

// Types
import { Announcement, AnnouncementPayload, AnnouncementSource } from '../../../../redux/modules/types/announcements';

// Redux
import { addAnnouncement } from '../../../../redux/modules/announcements/admin';

// Utils
import { formatMessage, FormattedMessage, LOCALE } from '../../../../utils/i18n';

// Components
import { Button, CancelButton, CloseButton, IconButton, PrimaryButton } from '../../buttons';
import { FormCheckbox, FormInput, FormTextArea } from '../../forms';
import { Tooltip } from '../../tooltips';
import Modal from '@/views/components/modals/AccessibleModal';

type DispatchProps = {
  addAnnouncement: (values: AnnouncementPayload) => Promise<Announcement>;
};

type Props = FormikProps<AnnouncementPayload> &
  DispatchProps & {
    onCancel?: () => void;
    show?: boolean;
  };

type State = {
  currentStep: CreateAnnouncementSteps;
  currentLocale: LOCALE;
};

enum CreateAnnouncementSteps {
  COMPOSE,
  PREVIEW,
  EDIT,
}

class CreateAnnoucementModal extends React.Component<Props, State> {
  readonly state: State = {
    currentStep: CreateAnnouncementSteps.COMPOSE,
    currentLocale: LOCALE.EN_CA,
  };

  locales: Array<LOCALE> = [LOCALE.EN_CA, LOCALE.FR_CA];

  closeModal = () => {
    const { resetForm, onCancel } = this.props;
    resetForm();
    onCancel && onCancel();
  };

  resetState = () => {
    this.setState({
      currentStep: CreateAnnouncementSteps.COMPOSE,
      currentLocale: LOCALE.EN_CA,
    });
  };

  canSubmit = () => {
    const { values, initialValues, isSubmitting, errors } = this.props;

    const hasErrors = Object.keys(errors).length > 0;
    const isDirty = values !== initialValues;

    return !hasErrors && isDirty && !isSubmitting;
  };

  hasLocaleFieldsFilled = (locale: LOCALE) => {
    const { errors, dirty } = this.props;

    // Counts errors only for passed locale
    const hasErrors =
      Object.entries(errors)
        .filter(([key]) => ['body', 'title'].includes(key))
        .filter(([, error]) => Object.keys(error as string).includes(locale)).length > 0;

    return dirty && !hasErrors;
  };

  goToNextStep = () => {
    const { currentStep, currentLocale } = this.state;
    const currentLocaleIndex = this.locales.indexOf(currentLocale);

    switch (currentStep) {
      case CreateAnnouncementSteps.COMPOSE:
        if (currentLocaleIndex < this.locales.length - 1) {
          this.setState({
            currentLocale: this.locales[currentLocaleIndex + 1],
          });
        } else {
          this.setState({
            currentStep: CreateAnnouncementSteps.PREVIEW,
          });
        }
        break;
      case CreateAnnouncementSteps.EDIT:
        this.setState({
          currentStep: CreateAnnouncementSteps.PREVIEW,
        });
        break;
    }
  };

  goToPrevStep = () => {
    const { currentStep, currentLocale } = this.state;
    const currentLocaleIndex = this.locales.indexOf(currentLocale);

    switch (currentStep) {
      case CreateAnnouncementSteps.COMPOSE:
        if (currentLocaleIndex > 0) {
          this.setState({
            currentLocale: this.locales[currentLocaleIndex - 1],
          });
        }
        break;
    }
  };

  editLocale = (locale: LOCALE) => {
    this.setState({
      currentLocale: locale,
      currentStep: CreateAnnouncementSteps.EDIT,
    });
  };

  renderStep = () => {
    const { onCancel, values, isSubmitting } = this.props;
    const { currentStep, currentLocale } = this.state;
    const currentLocaleIndex = this.locales.indexOf(currentLocale);

    switch (currentStep) {
      case CreateAnnouncementSteps.COMPOSE:
      case CreateAnnouncementSteps.EDIT:
        return (
          <>
            <Modal.Header>
              <Modal.Title>
                <FormattedMessage id="AnnouncementsAdmin.Create.Title" />
                <span className="locale ms-3">
                  <i className="icon-globe" />
                  <FormattedMessage id={`Common.Language.${currentLocale}`} />
                </span>
              </Modal.Title>

              {!!onCancel && <CloseButton onClick={this.closeModal} />}
            </Modal.Header>
            <Modal.Body>
              <FormInput
                name={`title['${currentLocale}']`}
                type="text"
                label={formatMessage('AnnouncementsAdmin.Create.Form.Title.Label')}
                autoComplete="off"
                placeholder={formatMessage('AnnouncementsAdmin.Create.Form.Title.Placeholder')}
              />
              <FormTextArea
                name={`body['${currentLocale}']`}
                label={formatMessage('AnnouncementsAdmin.Create.Form.Body.Label')}
                className="mt-4"
                autoComplete="off"
                placeholder={formatMessage('AnnouncementsAdmin.Create.Form.Body.Placeholder')}
                rows={6}
              />
            </Modal.Body>
            <Modal.Footer>
              {currentLocaleIndex > 0 && currentStep === CreateAnnouncementSteps.COMPOSE && (
                <Button onClick={this.goToPrevStep}>
                  <i className="icon-arrow-left" />
                  <FormattedMessage id="AnnouncementsAdmin.Create.Form.Previous" />
                </Button>
              )}
              <CancelButton onClick={this.closeModal} />
              <PrimaryButton onClick={this.goToNextStep} disabled={!this.hasLocaleFieldsFilled(currentLocale)}>
                {currentStep === CreateAnnouncementSteps.EDIT ? (
                  <FormattedMessage id="AnnouncementsAdmin.Create.Form.Preview" />
                ) : (
                  <>
                    <FormattedMessage id="AnnouncementsAdmin.Create.Form.Next" />
                    <i className="icon-arrow-right" />
                  </>
                )}
              </PrimaryButton>
            </Modal.Footer>
          </>
        );
      case CreateAnnouncementSteps.PREVIEW:
        return (
          <>
            <Modal.Header>
              <Modal.Title>
                <FormattedMessage id="AnnouncementsAdmin.Create.Title" />
                <div className="subtitle">
                  <FormattedMessage id="AnnouncementsAdmin.Create.Form.Preview" />
                </div>
              </Modal.Title>
              {!!onCancel && <CloseButton onClick={this.closeModal} />}
            </Modal.Header>
            <Modal.Body>
              {this.locales.map(locale => (
                <div className="locale-preview" key={locale}>
                  <div className="d-flex mb-3 justify-content-between">
                    <div className="locale">
                      <i className="icon-globe" />
                      <FormattedMessage id={`Common.Language.${locale}`} />
                    </div>
                    <IconButton icon="pencil" onClick={() => this.editLocale(locale)} aria-label={formatMessage('AnnouncementsAdmin.Create.Form.Edit')} />
                  </div>

                  <div className="preview-title">{values.title[locale]}</div>
                  <PerfectScrollbar className="preview-body-container">
                    <p className="preview-body">{values.body[locale]}</p>
                  </PerfectScrollbar>
                </div>
              ))}
              <div className="acknowledgment d-flex mt-4">
                <FormCheckbox
                  className="me-2"
                  name="acknowledgmentRequired"
                  label={formatMessage('AnnouncementsAdmin.Create.Form.AcknowledgmentRequired.Label')}
                />
                <Tooltip message={formatMessage('AnnouncementsAdmin.Create.Form.AcknowledgmentRequired.Tooltip.Message')}>
                  <i className="icon-exclamation" />
                </Tooltip>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <CancelButton onClick={this.closeModal} />
              <PrimaryButton className="submit-button" disabled={!this.canSubmit()} loading={isSubmitting} type="submit">
                {!isSubmitting && (
                  <>
                    <FormattedMessage id="AnnouncementsAdmin.Create.Form.Submit" />
                    <i className="icon-announcement" />
                  </>
                )}
              </PrimaryButton>
            </Modal.Footer>
          </>
        );
    }
  };

  render() {
    const { show, onCancel } = this.props;

    return (
      <Modal centered show={show} onHide={onCancel} onExited={this.resetState} className="add-announcement-modal modal">
        <Form>{this.renderStep()}</Form>
      </Modal>
    );
  }
}

const CreateAnnouncement = Yup.object().shape({
  body: Yup.object({
    'en-CA': Yup.string().trim().required(),
    'fr-CA': Yup.string().trim().required(),
  }),
  title: Yup.object({
    'en-CA': Yup.string().trim().required(),
    'fr-CA': Yup.string().trim().required(),
  }),
});

const formikWrapper = withFormik<Props, AnnouncementPayload>({
  validationSchema: CreateAnnouncement,
  mapPropsToValues: () => ({
    acknowledgmentRequired: false,
    body: { 'en-CA': '', 'fr-CA': '' },
    source: AnnouncementSource.ENTERPRISE,
    title: { 'en-CA': '', 'fr-CA': '' },
  }),
  handleSubmit: (values, { props, resetForm, setSubmitting }) => {
    props
      .addAnnouncement(values)
      .then(() => {
        resetForm();
        props.onCancel && props.onCancel();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default connect(null, {
  addAnnouncement,
})(formikWrapper(CreateAnnoucementModal)) as any;
